import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

export const useUnread = () =>
  useQuery({ queryKey: ["unread"], queryFn: () => api.get("/messages/unread").then((r) => r.data), refetchInterval: 30000 });

function Thread({ bookingId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["messages", bookingId], refetchInterval: 8000,
    queryFn: () => api.get(`/bookings/${bookingId}/messages`).then((r) => { qc.invalidateQueries({ queryKey: ["unread"] }); return r.data; }),
  });
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/messages`, { body: text });
      setText("");
      qc.invalidateQueries({ queryKey: ["messages", bookingId] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const msgs = data?.messages || [];
  return (
    <div className="space-y-3" data-testid={`message-thread-${bookingId}`}>
      <div className="max-h-[50vh] space-y-2 overflow-y-auto rounded-2xl bg-stone-50 p-3">
        {!msgs.length && <p className="py-6 text-center text-sm text-stone-500" data-testid="message-empty">No messages yet. Say hello.</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <p className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-white" : "border bg-white"}`} data-testid="message-bubble">{m.body}</p>
            </div>
          );
        })}
      </div>
      {data?.contact_hidden && <p className="text-xs text-stone-500" data-testid="message-contact-note">Phone numbers, emails and links are hidden until the stay is paid, so your payment stays protected.</p>}
      <div className="flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000} placeholder="Write a message" className="rounded-2xl" data-testid="message-input" />
        <Button onClick={send} disabled={busy || !text.trim()} className="h-auto rounded-2xl" data-testid="message-send-button" aria-label="Send message"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

// Guest-host conversation for one booking (opened from Trips or Hosting).
export function BookingMessages({ bookingId, unread = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid={`messages-open-${bookingId}`}>
          <MessageCircle className="mr-1 h-4 w-4" />Messages
          {unread > 0 && <span className="ml-1 rounded-full bg-primary px-1.5 text-xs text-white" data-testid={`messages-unread-${bookingId}`}>{unread}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-3xl">
        <DialogHeader><DialogTitle>Messages</DialogTitle></DialogHeader>
        {open && <Thread bookingId={bookingId} />}
      </DialogContent>
    </Dialog>
  );
}

export function MessagesButton({ bookingId }) {
  const { data: unread } = useUnread();
  return <BookingMessages bookingId={bookingId} unread={unread?.[bookingId] || 0} />;
}
